/*
 * @Copyright 2023-2024. Institute for Future Intelligence, Inc.
 */

import { Undoable } from './Undoable';

export class UndoManager {
  private commands: Undoable[];
  private index: number;
  private limit: number;
  private isExecuting: boolean;

  constructor() {
    this.commands = [];
    this.index = -1;
    this.limit = 0;
    this.isExecuting = false;
  }

  // Add a command to the queue
  add(command: Undoable): void {
    if (this.isExecuting) {
      return;
    }
    // if we are here after having called undo, invalidate items higher on the stack
    this.commands.splice(this.index + 1, this.commands.length - this.index);
    this.commands.push(command);
    // if limit is set, remove items from the start of the commands array
    if (this.limit > 0 && this.commands.length > this.limit) {
      this.commands.splice(0, this.commands.length - this.limit);
    }
    // set the current index to the end
    this.index = this.commands.length - 1;
  }

  // Call the undo function at the current index and decrease the index by 1.
  undo(): string | undefined {
    const command = this.commands[this.index];
    if (!command || command.blocked?.()) {
      return undefined;
    }
    this.isExecuting = true;
    // finally so a throwing command can't wedge the manager: a stuck
    // isExecuting=true makes every later add() a silent no-op, which would
    // stop ALL subsequent actions from registering undo entries.
    try {
      command.undo();
    } finally {
      this.isExecuting = false;
    }
    this.index--;
    return command.name;
  }

  // Call the redo function at the next index and increase the index by 1.
  redo(): string | undefined {
    const command = this.commands[this.index + 1];
    if (!command || command.blocked?.()) {
      return undefined;
    }
    this.isExecuting = true;
    try {
      command.redo();
    } finally {
      this.isExecuting = false;
    }
    this.index++;
    return command.name;
  }

  // Capture the whole stack (undo AND redo side) so a temporary operation that
  // goes through user-facing, undo-recording paths — an agent sweep that drops
  // molecules and then restores the lab itself — can put it back exactly,
  // including the redo branch its first add() would have discarded.
  checkpoint(): { commands: Undoable[]; index: number } {
    return { commands: this.commands.slice(), index: this.index };
  }

  restoreCheckpoint(cp: { commands: Undoable[]; index: number }): void {
    this.commands = cp.commands.slice();
    this.index = cp.index;
  }

  // Clears the memory, losing all stored states. Reset the index.
  clear(): void {
    this.commands = [];
    this.index = -1;
  }

  // The next undo / redo exists but may not run right now (see Undoable.blocked).
  undoBlocked(): boolean {
    return !!this.commands[this.index]?.blocked?.();
  }

  redoBlocked(): boolean {
    return !!this.commands[this.index + 1]?.blocked?.();
  }

  hasUndo(): boolean {
    return this.index !== -1;
  }

  hasRedo(): boolean {
    return this.index < this.commands.length - 1;
  }

  // Whether `command` is the entry the next undo would revert — lets a caller
  // fold a burst of small edits (a held nudge key) into one entry.
  isLastUndo(command: Undoable): boolean {
    return this.index >= 0 && this.commands[this.index] === command;
  }

  getLastUndoName(): string | undefined {
    const command = this.commands[this.index];
    if (!command) {
      return undefined;
    }
    return command.name;
  }

  getLastRedoName(): string | undefined {
    const command = this.commands[this.index + 1];
    if (!command) {
      return undefined;
    }
    return command.name;
  }

  // Names + timestamps of the currently-active (not undone-away) commands whose
  // timestamp is in (since, until]. Powers the agent's getRecentChanges tool:
  // the named log of undoable edits the user made between two agent replies.
  // Only commands at or below the current index count — anything the user undid
  // is no longer "an edit that happened". Oldest first.
  getEntriesSince(since: number, until: number = Number.POSITIVE_INFINITY): { name: string; timestamp: number }[] {
    const out: { name: string; timestamp: number }[] = [];
    for (let i = 0; i <= this.index; i++) {
      const c = this.commands[i];
      if (c && c.timestamp > since && c.timestamp <= until) {
        out.push({ name: c.name, timestamp: c.timestamp });
      }
    }
    return out;
  }

  setLimit(l: number): void {
    this.limit = l;
  }
}
